import type { AutoMinerDef, Player, ResourceCost, ResourceId } from './types';

export const MINERS: Record<string, AutoMinerDef> = {
  basic_clicker: {
    id: 'basic_clicker',
    nameKey: 'basic_clicker',
    descriptionKey: 'basic_clicker_desc',
    tier: 1,
    baseCost: { credits: 25 },
    costMultiplier: 1.3,
    yield: { credits: 1 },
    tickSeconds: 1,
    unlockLevel: 1,
  },
  scrap_hound: {
    id: 'scrap_hound',
    nameKey: 'scrap_hound',
    descriptionKey: 'scrap_hound_desc',
    tier: 1,
    baseCost: { credits: 150 },
    costMultiplier: 1.3,
    yield: { scrap: 2 },
    tickSeconds: 3,
    unlockLevel: 2,
  },
  chip_miner: {
    id: 'chip_miner',
    nameKey: 'chip_miner',
    descriptionKey: 'chip_miner_desc',
    tier: 2,
    baseCost: { credits: 1500 },
    costMultiplier: 1.4,
    yield: { nano_chip: 1 },
    tickSeconds: 8,
    unlockLevel: 5,
  },
  data_harvester: {
    id: 'data_harvester',
    nameKey: 'data_harvester',
    descriptionKey: 'data_harvester_desc',
    tier: 3,
    baseCost: { credits: 12000, scrap: 20 },
    costMultiplier: 1.5,
    yield: { data_track: 1 },
    tickSeconds: 25,
    unlockLevel: 12,
  },
  quantum_rig: {
    id: 'quantum_rig',
    nameKey: 'quantum_rig',
    descriptionKey: 'quantum_rig_desc',
    tier: 4,
    baseCost: { credits: 80000, nano_chip: 10 },
    costMultiplier: 1.6,
    yield: { hyper_processor_5: 1 },
    tickSeconds: 100,
    unlockLevel: 25,
  },
};

export function getMiner(id: string): AutoMinerDef | undefined {
  return MINERS[id];
}

export const OFFLINE_CAP_HOURS = 8;
const OFFLINE_CAP_SEC = OFFLINE_CAP_HOURS * 3600;

/** Cost of the Nth additional purchase (n = ownedCount, so first-ever purchase uses n=0). */
export function minerCost(def: AutoMinerDef, ownedCount: number): ResourceCost {
  const mult = Math.pow(def.costMultiplier, ownedCount);
  const out: ResourceCost = {};
  for (const [k, v] of Object.entries(def.baseCost) as Array<[keyof ResourceCost, number]>) {
    if (!v) continue;
    out[k] = Math.ceil(v * mult) as never; // TS workaround for mixed key types
  }
  return out;
}

/**
 * Calculate yield per second for display purposes.
 * Only counts resources (not credits) for the "yield" display; credits are reported separately.
 */
export function calcYieldPerSec(miners: Record<string, number>): Partial<Record<ResourceId | 'credits', number>> {
  const perSec: Partial<Record<ResourceId | 'credits', number>> = {};
  for (const [minerId, count] of Object.entries(miners)) {
    if (count <= 0) continue;
    const def = MINERS[minerId];
    if (!def) continue;
    for (const [resKey, amt] of Object.entries(def.yield) as Array<[ResourceId | 'credits', number]>) {
      const perTick = (amt * count) / def.tickSeconds;
      perSec[resKey] = (perSec[resKey] ?? 0) + perTick;
    }
  }
  return perSec;
}

/**
 * Apply offline progression between lastTickAt and now.
 * Returns accrued yields (capped at OFFLINE_CAP_HOURS).
 * Does not mutate — returns a delta the caller can apply.
 */
export function accrueOfflineYield(
  miners: Record<string, number>,
  lastTickAt: number,
  now: number = Date.now(),
): Partial<Record<ResourceId | 'credits', number>> {
  const elapsedSec = Math.max(0, Math.min(OFFLINE_CAP_SEC, (now - lastTickAt) / 1000));
  if (elapsedSec < 1) return {};

  const delta: Partial<Record<ResourceId | 'credits', number>> = {};
  for (const [minerId, count] of Object.entries(miners)) {
    if (count <= 0) continue;
    const def = MINERS[minerId];
    if (!def) continue;
    const ticks = Math.floor(elapsedSec / def.tickSeconds);
    if (ticks <= 0) continue;
    for (const [resKey, amt] of Object.entries(def.yield) as Array<[ResourceId | 'credits', number]>) {
      delta[resKey] = (delta[resKey] ?? 0) + ticks * amt * count;
    }
  }
  return delta;
}

/** How many of miner `id` does the player own? */
export function ownedCount(player: Player, id: string): number {
  return player.autoMiners[id] ?? 0;
}
